import { MenuItem, TextField, Stack } from "@mui/material";
import { DeleteBatchGroupAction } from "../components/batch/DeleteBatchGroupAction";
import { SourceCard } from "../components/batch/SourceCard";
import { AddSyncGroupDialog } from "../components/sync/AddSyncGroupDialog";
import { useSnackbar } from "notistack";
import {
  SourceGroupPanel,
  SourceSystemsContext,
  useSourceGroups,
  navigateToSystem,
} from "../components/sync/SourceSyncGroups";
import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  BatchGroupDetails,
  BatchWindowConfig,
  getAllBatchGroupDetails,
  getBatchWindowConfig,
} from "../api/batch";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { BatchComponent } from "../components/batch/Batch";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { PageTitle } from "../components/utils/PageTitle";
import {
  BundledDataLoader,
  DataLoaderOption,
  getAllDataLoaderSystems,
  getAvailableDataTypesAndOptions,
  getStandaloneSyncSystems,
  StandaloneSyncSystem,
} from "../api/datasystems";
import { StandaloneSyncSystemCard } from "../components/batch/StandaloneSyncSystemCard";

export function Configuration() {
  const { userProfile } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [addGroup, setAddGroup] = useState(false);
  const groups = useSourceGroups(
    ["CIRROM_ADMIN", "SUPER_ADMIN"].includes(userProfile)
  );
  const [batchWindowConfig, setBatchWindowConfig] =
    useState<BatchWindowConfig>();

  const [configuredBatchGroups, setConfiguredBatchGroups] = useState<
    BatchGroupDetails[]
  >([]);
  const [addNewItem, setAddNewItem] = useState<boolean>(false);

  const [dataLoaderOptions, setDataLoaderOptions] = useState<
    DataLoaderOption[]
  >([]);
  const [syncSystemOptions, setSyncSystemOptions] = useState<
    DataLoaderOption[]
  >([]);

  const [availableDataLoaders, setAvailableDataLoaders] = useState<
    BundledDataLoader[]
  >([]);
  const [standaloneSyncSystems, setStandaloneSyncSystems] = useState<
    StandaloneSyncSystem[]
  >([]);

  const refreshConfiguration = () => {
    getAllBatchGroupDetails()
      .then(async (batchGroupDetail) => {
        setConfiguredBatchGroups(
          Array.from(new Map(batchGroupDetail.map((b) => [b.id, b])).values())
        );
      })
      .catch((err) => console.log(err));
    getAllDataLoaderSystems()
      .then((dataLoaderResponse) => {
        setAvailableDataLoaders(dataLoaderResponse);
      })
      .catch((err) => console.log(err));
    getStandaloneSyncSystems()
      .then((syncSystems) => {
        setStandaloneSyncSystems(syncSystems);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    let isMounted = true;
    refreshConfiguration();
    getBatchWindowConfig()
      .then((response) => {
        if (isMounted) {
          setBatchWindowConfig(response);
        }
      })
      .catch((err) => console.log(err));
    getAvailableDataTypesAndOptions()
      .then((dataOptionsResponse) => {
        if (isMounted) {
          setDataLoaderOptions(dataOptionsResponse.CRM || []);
          setSyncSystemOptions(dataOptionsResponse.SYNC || []);
        }
      })
      .catch((err) => console.log(err));

    return () => {
      isMounted = false;
    };
  }, []);

  const systemsForRevision = (revision: number) =>
    groups.data.system_revisions.filter(
      (s) => s.config_revision_id === revision
    );
  const visibleIds = new Set<number>([
    ...configuredBatchGroups.flatMap((b) =>
      systemsForRevision(b.data_loader_config_revision_id).map(
        (s) => s.system_id
      )
    ),
    ...configuredBatchGroups.map((b) => b.data_loader_id),
    ...standaloneSyncSystems.map((s) => s.id),
  ]);
  const jumpSystems = Array.from(
    new Map(
      [
        ...availableDataLoaders.map((s) => s.DataLoaderSystem),
        ...groups.data.system_revisions,
        ...standaloneSyncSystems,
      ]
        .filter((s) => visibleIds.has(s.id))
        .map((s) => [s.id, s])
    ).values()
  );
  const panel = (ids: number[]) => (
    <SourceGroupPanel
      groups={groups.data.groups.filter(
        (g) => ids.includes(g.system_a.id) || ids.includes(g.system_b.id)
      )}
      visibleIds={visibleIds}
      systemIds={ids}
      loading={groups.loading}
      error={groups.error}
      retry={groups.refresh}
    />
  );

  const canAddNewItem = () => {
    if (addNewItem) {
      return (
        <BatchComponent
          batchWindowConfig={batchWindowConfig}
          status="New"
          dataLoaderOptions={dataLoaderOptions}
          syncSystemOptions={syncSystemOptions}
          availableDataLoaders={availableDataLoaders}
          onSaved={() => {
            setAddNewItem(false);
            refreshConfiguration();
          }}
        />
      );
    }
  };

  return (
    <div>
      <PageTitle
        pageTitle="Configuration | CIRROM"
        pageHeader={
          configuredBatchGroups.length + standaloneSyncSystems.length > 0
            ? "Configured Data Sources"
            : "Set up a new Data Source"
        }
      >
        <Container maxWidth="xl" style={{ padding: "0px" }}>
          {["CIRROM_ADMIN", "SUPER_ADMIN"].includes(userProfile) ? (
            <>
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1.5}
                sx={{ mb: 2 }}
                alignItems="center"
              >
                {jumpSystems.length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Jump to system"
                    value=""
                    onChange={(event) =>
                      navigateToSystem(Number(event.target.value))
                    }
                    sx={{
                      width: { xs: "100%", sm: 220 },
                      maxWidth: { sm: 320 },
                    }}
                  >
                    {jumpSystems.map((system) => (
                      <MenuItem key={system.id} value={system.id}>
                        {system.name} · #{system.id}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => setAddNewItem(!addNewItem)}
                >
                  Add data source
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={() => setAddGroup(true)}
                  sx={{ textTransform: "none" }}
                >
                  Add sync group
                </Button>
              </Stack>
              <AddSyncGroupDialog
                open={addGroup}
                onClose={() => setAddGroup(false)}
                onCreated={() => {
                  setAddGroup(false);
                  enqueueSnackbar("Sync group created", { variant: "success" });
                  void groups.refresh();
                }}
              />
              {configuredBatchGroups.length + standaloneSyncSystems.length >
              0 ? (
                <>
                  <Container
                    maxWidth="xl"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px",
                      padding: 0,
                    }}
                  >
                    {canAddNewItem()}
                    {configuredBatchGroups.map((configuredBatchGroup) => {
                      const primary = availableDataLoaders.find(
                        (s) =>
                          s.DataLoaderConfigRevision.id ===
                          configuredBatchGroup.data_loader_config_revision_id
                      )?.DataLoaderSystem;
                      const systemId =
                        primary?.id ?? configuredBatchGroup.data_loader_id;
                      return (
                        batchWindowConfig && (
                          <SourceCard
                            key={`Batch-${configuredBatchGroup.id}`}
                            systemId={systemId}
                            name={primary?.name || configuredBatchGroup.name}
                            status={configuredBatchGroup.last_batch_status}
                            action={
                              <DeleteBatchGroupAction
                                id={configuredBatchGroup.id}
                                name={
                                  primary?.name || configuredBatchGroup.name
                                }
                                onDeleted={refreshConfiguration}
                              />
                            }
                            groups={panel([
                              systemId,
                              ...systemsForRevision(
                                configuredBatchGroup.data_loader_config_revision_id
                              ).map((s) => s.system_id),
                            ])}
                          >
                            <SourceSystemsContext.Provider
                              value={systemsForRevision(
                                configuredBatchGroup.data_loader_config_revision_id
                              )}
                            >
                              <BatchComponent
                                embedded
                                subheader={configuredBatchGroup.name}
                                existing={true}
                                status={configuredBatchGroup.last_batch_status}
                                id={configuredBatchGroup.id}
                                configRevisionId={
                                  configuredBatchGroup.data_loader_config_revision_id
                                }
                                ruleGroupId={configuredBatchGroup.rule_group_id}
                                batchWindowConfig={batchWindowConfig}
                                dataLoaderOptions={dataLoaderOptions}
                                syncSystemOptions={syncSystemOptions}
                                availableDataLoaders={availableDataLoaders}
                                onSaved={refreshConfiguration}
                              />
                            </SourceSystemsContext.Provider>
                          </SourceCard>
                        )
                      );
                    })}
                    {standaloneSyncSystems.map((syncSystem) => {
                      return (
                        <SourceCard
                          key={`Sync-${syncSystem.id}`}
                          systemId={syncSystem.id}
                          name={syncSystem.name}
                          type={syncSystem.system_type}
                          groups={panel([syncSystem.id])}
                        >
                          <StandaloneSyncSystemCard
                            syncSystem={syncSystem}
                            syncSystemOptions={syncSystemOptions}
                          />
                        </SourceCard>
                      );
                    })}
                    {groups.data.groups.some(
                      (g) =>
                        !visibleIds.has(g.system_a.id) &&
                        !visibleIds.has(g.system_b.id)
                    ) && (
                      <SourceGroupPanel
                        groups={groups.data.groups.filter(
                          (g) =>
                            !visibleIds.has(g.system_a.id) &&
                            !visibleIds.has(g.system_b.id)
                        )}
                        visibleIds={visibleIds}
                        loading={groups.loading}
                        error={groups.error}
                        retry={groups.refresh}
                      />
                    )}
                  </Container>
                </>
              ) : (
                <Container
                  maxWidth="xl"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  {batchWindowConfig && (
                    <BatchComponent
                      batchWindowConfig={batchWindowConfig}
                      dataLoaderOptions={dataLoaderOptions}
                      syncSystemOptions={syncSystemOptions}
                      status="New"
                      availableDataLoaders={availableDataLoaders}
                      onSaved={refreshConfiguration}
                    />
                  )}
                  <SourceGroupPanel
                    groups={groups.data.groups}
                    visibleIds={visibleIds}
                    loading={groups.loading}
                    error={groups.error}
                    retry={groups.refresh}
                  />
                </Container>
              )}
            </>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  Access Restricted
                </Typography>
                <Typography variant="body1" style={{ marginTop: "10px" }}>
                  You do not have permission to view this page.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Container>
      </PageTitle>
    </div>
  );
}
